// Video Director "Story & assets" overlay: the story, cast, locations, keyframes and the reference set
// at a glance, with one-click reuse of any image (add to the reference set, use as a keyframe, start a
// new shot from it). Presentational: VideoDirector / DirectorEditStep own the API calls.

import React, { useState } from "react";
import i18n from "i18next";
import { LuImage, LuImagePlus, LuPencil, LuPlus } from "react-icons/lu";
import { MAX_REFS } from "./edl";
import { OverlayPanel, RefImage } from "./DirectorPanels";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);

const TABS = ["Story", "Cast", "Locations", "Keyframes", "Refs"];
const BRIEF = ["learningGoal", "audience", "language", "targetSec"];
const btn = { fontSize: 12, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4 };
const readyUrl = (item) => (item?.status === "ready" && item.url ? item.url : null);

const USE_MODES = [
  ["all", "UseAll"],
  ["pick", "UsePick"],
  ["off", "UseOff"],
];

/** Brief step: how much of the course cookbook this video uses + whether new characters/places are saved to it. */
export function CookbookControl({ cookbook }) {
  const has = cookbook.data.cast.length + cookbook.data.locations.length > 0;
  const use = cookbook.use ?? "pick";
  const lock = !cookbook.canExclude;
  return (
    <div style={{ marginBottom: 12 }}>
      {has && (
        <div className="add-video-modal__ai-section">
          <div className="add-video-modal__ai-label">{t("Cookbook.UseLabel")}</div>
          <div
            className="add-video-modal__ai-ratio"
            role="group"
            aria-label={t("Cookbook.UseLabel")}
          >
            {USE_MODES.map(([id, key]) => (
              <button
                key={id}
                type="button"
                disabled={lock}
                aria-pressed={use === id}
                onClick={() => cookbook.onUse(id)}
                className={`add-video-modal__ai-chip${use === id ? " add-video-modal__ai-chip--active" : ""}`}
              >
                {t(`Cookbook.${key}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input
          type="checkbox"
          disabled={lock}
          checked={cookbook.saveNew}
          onChange={(ev) => cookbook.onSaveNew(ev.target.checked)}
        />
        {t("Cookbook.SaveNew")}
      </label>
    </div>
  );
}

/**
 * "From this course": the training's shared cast / locations (the course cookbook).
 * cookbook = { data: { cast, locations }, use: 'all' | 'pick' | 'off' (default 'pick'), exclude: ids this video must
 * not use (only honoured by 'pick'), canExclude, onToggle(id), onRename(id, name), onRemove(id) }.
 * 'pick' shows a checkbox per entry (checked = used), 'all' lists read-only, 'off' hides the list.
 * Rename and remove confirm inline (no popup over a popup).
 */
export function CookbookSection({ kind, cookbook }) {
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [removing, setRemoving] = useState(null); // entry id
  const entries = cookbook?.data?.[kind] || [];
  const use = cookbook?.use ?? "pick";
  if (!entries.length || use === "off") return null;
  const previews = entries.map((e) => ({
    id: `cookbook-${e.id}`,
    url: e.stillUrl,
    name: e.name,
    description: e.description,
  }));
  const button = (key, label, onClick, extra = {}) => (
    <button
      key={key}
      type="button"
      className={`add-video-modal__btn add-video-modal__btn--${extra.primary ? "primary" : "secondary"}`}
      style={btn}
      disabled={extra.disabled}
      onClick={onClick}
    >
      {extra.icon}
      {label}
    </button>
  );
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="add-video-modal__vd-label">{t("Cookbook.Title")}</div>
      <div
        className="add-video-modal__vd-cards add-video-modal__vd-assets"
        style={{ maxHeight: 260, overflowY: "auto" }}
      >
        {entries.map((e, i) => {
          const out = use === "pick" && cookbook.exclude.includes(e.id);
          return (
            <div
              key={e.id}
              className="add-video-modal__scene-card add-video-modal__vd-card"
              style={out ? { opacity: 0.6 } : undefined}
            >
              <RefImage
                item={{ status: "ready", url: e.stillUrl }}
                preview={{ items: previews, index: i }}
              />
              {renaming?.id === e.id ? (
                <input
                  autoFocus
                  value={renaming.name}
                  maxLength={60}
                  aria-label={t("Cookbook.Rename")}
                  style={{ fontSize: 12, padding: "3px 6px" }}
                  onChange={(ev) => setRenaming({ id: e.id, name: ev.target.value })}
                />
              ) : (
                <div className="add-video-modal__vd-name">{e.name}</div>
              )}
              {out && <div style={{ fontSize: 11, color: "#64748b" }}>{t("Cookbook.NotUsed")}</div>}
              <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--2">
                {e.description}
              </div>
              <div className="add-video-modal__vd-actions">
                {renaming?.id === e.id ? (
                  <>
                    {button(
                      "save",
                      t("Cookbook.Save"),
                      () => {
                        cookbook.onRename(e.id, renaming.name.trim());
                        setRenaming(null);
                      },
                      { primary: true, disabled: !renaming.name.trim() }
                    )}
                    {button("cancel", t("Cookbook.Cancel"), () => setRenaming(null))}
                  </>
                ) : removing === e.id ? (
                  <>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {t("Cookbook.ConfirmRemove")}
                    </div>
                    {button(
                      "yes",
                      t("Cookbook.ConfirmYes"),
                      () => {
                        cookbook.onRemove(e.id);
                        setRemoving(null);
                      },
                      { primary: true }
                    )}
                    {button("no", t("Cookbook.Cancel"), () => setRemoving(null))}
                  </>
                ) : (
                  <>
                    {cookbook.canExclude && use === "pick" && (
                      <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="checkbox"
                          checked={!out}
                          onChange={() => cookbook.onToggle(e.id)}
                        />
                        {t("Cookbook.Use")}
                      </label>
                    )}
                    {button(
                      "rename",
                      t("Cookbook.Rename"),
                      () => setRenaming({ id: e.id, name: e.name }),
                      {
                        icon: <LuPencil size={12} />,
                      }
                    )}
                    {button("remove", t("Cookbook.Remove"), () => setRemoving(e.id))}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * onAddRef(url) → ref id (the reference set copies our own storage url); omitted = the set is not editable
 * here. keyframeShot = { n } of the selected shot (Edit step); onUseKeyframe(refId) / onNewShot(prefill)
 * are omitted where they do not apply.
 */
export default function StoryAssetsPanel({
  project,
  onClose,
  onAddRef,
  keyframeShot,
  onUseKeyframe,
  onNewShot,
  busy,
  error,
  onDismissError,
  cookbook,
}) {
  const [tab, setTab] = useState("Story");
  const [working, setWorking] = useState(false);
  const refs = project.refs || [];
  const bible = project.bible;
  const images = {
    Cast: (bible?.cast || []).map((m) => ({ kind: "cast", id: m.id, item: m.still, ...m })),
    Locations: (bible?.locations || []).map((m) => ({
      kind: "location",
      id: m.id,
      item: m.still,
      ...m,
    })),
    Keyframes: project.shots.map((s, i) => ({
      kind: "keyframe",
      id: s.id,
      item: s.keyframe,
      name: t("Shot", { n: i + 1 }),
      description: s.keyframe.prompt,
    })),
    Refs: refs.map((r, i) => ({
      kind: "ref",
      id: r.id,
      item: r,
      name: t("RefN", { n: i + 1 }),
      description: r.prompt,
    })),
  };
  const refIdOf = (img) =>
    img.kind === "ref" ? img.id : refs.find((r) => r.url === readyUrl(img.item))?.id;
  // The ref id of an image, adding it to the reference set first when it is not in there yet.
  const ensureRef = async (img) => {
    const have = refIdOf(img);
    if (have) return have;
    setWorking(true);
    try {
      return await onAddRef(readyUrl(img.item));
    } finally {
      setWorking(false);
    }
  };
  const off = busy || working;

  // The card's actions, also shown in the preview lightbox.
  const actionsOf = (img) => {
    const url = readyUrl(img.item);
    const inSet = !!refIdOf(img);
    return [
      onAddRef &&
        img.kind !== "ref" && {
          key: "addRef",
          icon: LuImagePlus,
          label: inSet ? t("InRefSet") : t("AddToRefs"),
          disabled: off || !url || inSet || refs.length >= MAX_REFS,
          onClick: () => onAddRef(url),
        },
      onUseKeyframe &&
        keyframeShot && {
          key: "useKeyframe",
          icon: LuImage,
          label: t("UseKeyframeFor", { n: keyframeShot.n }),
          disabled: off || !url,
          onClick: async () => {
            const id = await ensureRef(img);
            if (id) onUseKeyframe(id);
          },
        },
      onNewShot && {
        key: "newShot",
        icon: LuPlus,
        label: t("NewShotWith"),
        primary: true,
        disabled: off || (img.kind !== "cast" && img.kind !== "location" && !url),
        onClick: async () => {
          if (img.kind === "cast") return onNewShot({ castIds: [img.id] });
          if (img.kind === "location") return onNewShot({ locationId: img.id });
          const id = await ensureRef(img);
          return id && onNewShot({ keyframeRefId: id });
        },
      },
    ].filter(Boolean);
  };

  const tile = (img, previews) => {
    const at = previews.findIndex((p) => p.id === `${img.kind}-${img.id}`);
    return (
      <div key={`${img.kind}-${img.id}`} className="add-video-modal__scene-card add-video-modal__vd-card">
        {img.item ? (
          <RefImage item={img.item} preview={{ items: previews, index: at }} />
        ) : (
          <div className="add-video-modal__director-ref-img" />
        )}
        <div className="add-video-modal__vd-name">{img.name}</div>
        {img.sourceRefId && <div style={{ fontSize: 11, color: "#7c3aed" }}>{t("FromPhoto")}</div>}
        {(img.kind === "cast" || img.kind === "location") &&
          (cookbook?.data?.[img.kind === "cast" ? "cast" : "locations"] || []).some(
            (e) => e.id === img.id
          ) && <div style={{ fontSize: 11, color: "#0f766e" }}>{t("Cookbook.Badge")}</div>}
        {img.description && (
          <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--2">
            {img.description}
          </div>
        )}
        <div className="add-video-modal__vd-actions">
          {actionsOf(img).map(({ key, icon: Icon, label, primary, disabled, onClick }) => (
            <button
              key={key}
              type="button"
              className={`add-video-modal__btn add-video-modal__btn--${primary ? "primary" : "secondary"}`}
              style={btn}
              disabled={disabled}
              onClick={onClick}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const field = (label, value) =>
    value !== undefined && value !== null && value !== "" ? (
      <div key={label} className="add-video-modal__vd-block" style={{ padding: "8px 10px" }}>
        <div className="add-video-modal__vd-label">{label}</div>
        <div className="add-video-modal__vd-clamp" style={{ WebkitLineClamp: 6 }}>
          {value}
        </div>
      </div>
    ) : null;

  return (
    <OverlayPanel
      title={t("StoryAssets")}
      helper={t("StoryAssetsHelper")}
      onClose={onClose}
      error={error}
      onDismissError={onDismissError}
    >
      <div className="add-video-modal__tabs add-video-modal__tabs--inline" role="tablist" style={{ marginBottom: 10 }}>
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`add-video-modal__tab${tab === id ? " add-video-modal__tab--active" : ""}`}
          >
            <span className="add-video-modal__tab-label">{t(`Tabs.${id}`)}</span>
          </button>
        ))}
      </div>
      {tab === "Story" ? (
        <div className="add-video-modal__vd-cols">
          <div className="add-video-modal__vd-col">
            {field(t("Fields.logline"), bible?.logline)}
            {field(t("Fields.look"), bible?.look)}
            {BRIEF.map((key) => field(t(`Fields.${key}`), project.brief?.[key]))}
            {field(t("Aspect"), project.brief?.aspect)}
          </div>
          <div className="add-video-modal__vd-col">
            <div className="add-video-modal__vd-label">{t("ShotList")}</div>
            {project.shots.map((s, i) => (
              <div key={s.id} className="add-video-modal__scene-card add-video-modal__vd-row">
                <div className="add-video-modal__scene-title">
                  <span className="add-video-modal__scene-num">{t("Shot", { n: i + 1 })}</span>
                  <span className="add-video-modal__scene-time">{s.durationSec}s</span>
                </div>
                <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--2">
                  {s.prompt}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {(tab === "Cast" || tab === "Locations") && (
            <CookbookSection kind={tab === "Cast" ? "cast" : "locations"} cookbook={cookbook} />
          )}
          {images[tab].length === 0 ? (
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("NothingHere")}</div>
          ) : (
            <div className="add-video-modal__vd-cards add-video-modal__vd-assets">
              {(() => {
                const previews = images[tab]
                  .filter((img) => readyUrl(img.item))
                  .map((img) => ({
                    id: `${img.kind}-${img.id}`,
                    url: img.item.url,
                    name: img.name,
                    description: img.description,
                    actions: actionsOf(img),
                  }));
                return images[tab].map((img) => tile(img, previews));
              })()}
            </div>
          )}
        </>
      )}
    </OverlayPanel>
  );
}
